import { Link } from "react-router-dom";
import { Monitor, AlertTriangle, Shield, Layers } from "lucide-react";
import { useTenantContext } from "@/hooks/use-tenant-context";
import { useTenantCompliance } from "@/api/compliance";
import { useTenantAlerts } from "@/api/alerts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ComplianceBadge } from "@/components/compliance-badge";
import { Skeleton } from "@/components/ui/skeleton";

export function TenantDetailPage() {
  const { tenantId, tenant, isLoading: tenantLoading } = useTenantContext();
  const { data: compliance, isLoading: complianceLoading } = useTenantCompliance(tenantId);
  const { data: alertsData } = useTenantAlerts(tenantId, { "filter[status]": "open", pageSize: "5" });

  if (tenantLoading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><Skeleton className="h-64 w-full" /></div>;
  }

  const totalDevices = compliance?.total_devices ?? 0;
  const coverage = compliance?.source_coverage;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{tenant?.name}</h1>
          <p className="text-sm text-text-muted">{tenant?.slug} &middot; {tenant?.domain}</p>
        </div>
        <Badge variant={tenant?.status === "active" ? "success" : tenant?.status === "onboarding" ? "warning" : "danger"}>
          {tenant?.status}
        </Badge>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-accent/10 p-2"><Monitor className="h-4 w-4 text-accent" /></div>
            <div>
              <p className="text-xs text-text-muted">Devices</p>
              {complianceLoading ? <Skeleton className="h-5 w-10" /> : <p className="text-lg font-bold">{totalDevices}</p>}
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-success/10 p-2"><Shield className="h-4 w-4 text-success" /></div>
            <div>
              <p className="text-xs text-text-muted">Compliance</p>
              {complianceLoading ? <Skeleton className="h-5 w-10" /> : <ComplianceBadge score={compliance?.average_score ?? null} />}
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-danger/10 p-2"><AlertTriangle className="h-4 w-4 text-danger" /></div>
            <div>
              <p className="text-xs text-text-muted">Open Alerts</p>
              <p className="text-lg font-bold">{alertsData?.meta?.total ?? 0}</p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-info/10 p-2"><Layers className="h-4 w-4 text-info" /></div>
            <div>
              <p className="text-xs text-text-muted">Source Coverage</p>
              {complianceLoading || !coverage ? (
                <Skeleton className="h-5 w-20" />
              ) : (
                <div className="flex gap-2 text-xs">
                  <span>CS:{coverage.crowdstrike}</span>
                  <span>AT:{coverage.atera}</span>
                  <span>JC:{coverage.jumpcloud}</span>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {coverage && totalDevices > 0 && (
        <Card>
          <CardHeader><CardTitle>Source Coverage</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {([
                { label: "CrowdStrike", key: "crowdstrike" as const, color: "bg-accent" },
                { label: "Atera", key: "atera" as const, color: "bg-info" },
                { label: "JumpCloud", key: "jumpcloud" as const, color: "bg-success" },
              ]).map(({ label, key, color }) => (
                <div key={key} className="flex items-center gap-3">
                  <span className="w-28 text-sm text-text-muted">{label}</span>
                  <div className="flex-1 rounded-full bg-surface-hover h-2.5">
                    <div
                      className={`h-2.5 rounded-full ${color}`}
                      style={{ width: `${Math.round((coverage[key] / totalDevices) * 100)}%` }}
                    />
                  </div>
                  <span className="w-16 text-right text-sm font-medium">{coverage[key]}/{totalDevices}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <Link to={`/tenants/${tenantId}/devices`}>
          <Badge variant="accent" className="cursor-pointer px-3 py-1.5">Devices</Badge>
        </Link>
        <Link to={`/tenants/${tenantId}/alerts`}>
          <Badge variant="accent" className="cursor-pointer px-3 py-1.5">Alerts</Badge>
        </Link>
        <Link to={`/tenants/${tenantId}/settings`}>
          <Badge variant="accent" className="cursor-pointer px-3 py-1.5">Settings</Badge>
        </Link>
      </div>

      <Card>
        <CardHeader><CardTitle>Recent Alerts</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {alertsData?.data?.map((alert) => (
              <div key={alert.id} className="flex items-center justify-between rounded-lg p-3 hover:bg-surface-hover">
                <div>
                  <p className="text-sm font-medium">{alert.title}</p>
                  <p className="text-xs text-text-muted">{alert.alert_type}</p>
                </div>
                <Badge variant={alert.severity === "critical" ? "danger" : alert.severity === "warning" ? "warning" : "info"}>
                  {alert.severity}
                </Badge>
              </div>
            ))}
            {(!alertsData?.data || alertsData.data.length === 0) && (
              <p className="py-4 text-center text-sm text-text-muted">No open alerts.</p>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
